import { ref } from 'vue'
import { api } from '@/api'

/**
 * #1796: nothing runs without an AI account, in the portal too. Every page opens without one, so the one portal guard
 * stands in front of every action that would start model work instead: sending a chat message, starting or retrying a
 * run, cloning a team or a role, and approving work (a plan, a gate, the chat's decision card). Browsing, starring and
 * editing never come here.
 *
 * What the portal last knew about the enterprise's AI account: `unknown` until a read answers, or when the last read
 * failed. The shell's banner draws from it (shown only on `not_connected`), and so does the guard.
 */
export type AiAccountStatus = 'connected' | 'not_connected' | 'unknown'

/** Reactive, so the shell's banner and the prompt follow it without a navigation. */
const status = ref<AiAccountStatus>('unknown')
const promptOpen = ref(false)

/** The last known status. */
export function aiAccountStatus(): AiAccountStatus {
    return status.value
}

/** Records what a read of the account status answered, or what the connect card just did. */
export function setAiAccountStatus(next: AiAccountStatus): void {
    status.value = next
}

/** Whether the "Connect your AI account to start" prompt is open. */
export function aiAccountPromptOpen(): boolean {
    return promptOpen.value
}

/** Closes the prompt. */
export function closeAiAccountPrompt(): void {
    promptOpen.value = false
}

/**
 * Reads the status live and records it: `connected` or `not_connected` from the server's answer, `unknown` when the
 * read fails. Never throws.
 */
export async function readAiAccountStatus(): Promise<AiAccountStatus> {
    try {
        status.value = (await api.aiAccount()).connected === true ? 'connected' : 'not_connected'
    } catch {
        status.value = 'unknown'
    }
    return status.value
}

/** Whether a click-time re-read is still out. While one is, no other guarded action is taken. */
let rereading = false

/**
 * Asks before a model-starting action: true when it may go ahead, false when it is not taken.
 *
 * - The last known status is `connected`: it goes ahead with no extra request, so a connected founder pays nothing.
 * - Anything else is re-read at click time, so an account connected in another tab lets the work start at once.
 * - It fails closed: unless the re-read says connected, the action is blocked and the prompt opens. A status that
 *   cannot be read is treated as no account, because "nothing runs without an account" has to hold either way.
 * - One re-read at a time. A second guarded click while the first click's re-read is still out is not taken: it
 *   answers false and opens no prompt of its own. The handlers set their busy flags only once this answers, so
 *   without this a double-click during the round trip would send the request twice (two retries, two runs). Once
 *   the re-read has answered, the next click is judged as usual.
 * - That check comes before the `connected` fast path. Other reads also set the status (the page load's and the one
 *   after each navigation), so one of them can answer `connected` while a click's re-read is still out. Without
 *   this order the second click would go ahead on the fast path and the first would go ahead when its re-read
 *   answered: the request sent twice. A refused click makes no request, so a connected founder still pays nothing.
 */
export async function requireAiAccount(): Promise<boolean> {
    if (rereading) {
        return false
    }
    if (status.value === 'connected') {
        return true
    }
    rereading = true
    try {
        if ((await readAiAccountStatus()) === 'connected') {
            return true
        }
    } finally {
        rereading = false
    }
    promptOpen.value = true
    return false
}
